import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Search, 
  Calendar, 
  GraduationCap, 
  Building2, 
  Phone, 
  ShieldAlert, 
  HeartHandshake, 
  Trash2, 
  AlertCircle, 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown, 
  Check,
  Info,
  Users,
  FileCheck,
  ExternalLink,
  Eye, 
  Upload, 
  Mail, 
  UserCheck, 
  FileText,
  X
} from "lucide-react";
import { FiCalendar, FiChevronDown } from "react-icons/fi";
import toast, { Toaster } from "react-hot-toast";
import api from '../../connector/api';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';

export default function ModernRegistrarWorkspace() {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // User & Environment State
  const [permissions, setPermissions] = useState([]);
  const [user, setUser] = useState({ first_name: "Achmad", last_name: "Hakim", email: "achmadhakim@gmail.com" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [loadingPeriod, setLoadingPeriod] = useState(true);
  const notificationDropdownRef = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false);

  // Modals & Details State
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  // Local document state mocks
  const [birthCertificate, setBirthCertificate] = useState(null);
  const [transcript, setTranscript] = useState(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Today's Date
  const todayDate = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric"
  });

  useEffect(() => {
    const storedPerms = sessionStorage.getItem("permissions");
    const storedUser = sessionStorage.getItem("user");
    
    if (storedPerms) {
      try {
        setPermissions(JSON.parse(storedPerms));
      } catch (err) {
        console.error("Failed to parse permissions:", err);
      }
    }

    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        setUser({
          first_name: userObj.first_name || "Achmad",
          last_name: userObj.last_name || "Hakim",
          username: userObj.username || "achmadhakim",
          email: userObj.email || "achmadhakim@gmail.com"
        });
      } catch (err) {
        console.error("Failed to parse user session:", err);
      }
    }

    const fetchCurrentPeriod = async () => {
      try {
        const res = await api.get("year-terms/get-set-year-term/");
        setCurrentPeriod(res.data);
      } catch (err) {
        console.error("Failed to fetch active school year-term:", err);
      } finally {
        setLoadingPeriod(false);
      }
    };
    fetchCurrentPeriod();
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  const yearDisplay = currentPeriod?.school_year_detail?.name || currentPeriod?.school_year || "2025/2026";
  const termDisplay = currentPeriod?.term_detail?.name || currentPeriod?.term || "Term 1";

  // Fetch Applicants
  const fetchApplicants = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const params = {};
      if (searchQuery.trim() !== '') params.search = searchQuery;

      const response = await api.get(`registrar/`, { params });
      const data = Array.isArray(response.data) ? response.data : response.data.results || [];
      
      setApplicants(data);
      setCurrentPage(1);
      
      if (data.length > 0 && (!activeId || !data.some(a => a.id === activeId))) {
        setActiveId(data[0].id);
      }
    } catch (err) {
      console.error('Ledger sync failure:', err);
      setErrorMessage('Failed to load records from the cloud database server.');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, activeId]);

  useEffect(() => {
    fetchApplicants();
  }, [fetchApplicants]);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'birth_cert') setBirthCertificate(file);
      if (type === 'transcript') setTranscript(file);
      toast.success(`${file.name} attached successfully.`);
    }
  };

  const categoryCounts = useMemo(() => {
    return {
      ALL: applicants.length,
      PENDING: applicants.filter(app => app.application_status === 'PENDING').length,
      SCHEDULED: applicants.filter(app => app.application_status === 'SCHEDULED').length,
      ACCEPTED: applicants.filter(app => app.application_status === 'ACCEPTED').length,
    };
  }, [applicants]);

  const filteredApplicants = useMemo(() => {
    if (filterStatus === 'ALL') return applicants;
    return applicants.filter(app => app.application_status === filterStatus);
  }, [applicants, filterStatus]);

  const handleStatusChange = async (id, newStatus) => {
    setActionLoading(true);
    try {
      const response = await api.patch(`registrar/manage-applicants/${id}/`, {
        application_status: newStatus
      });
        
      if (response.status === 200) {
        setApplicants(prev => prev.map(item => item.id === id ? { ...item, application_status: newStatus } : item));
        toast.success(`Status updated to ${newStatus}`);
      }
    } catch (err) {
      console.error("Status update error:", err.response?.data);
      toast.error('Could not save status changes.');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDelete = async (id) => {
    setIsDeleting(true);
    try {
      await api.delete(`registrar/manage-applicants/${id}/`);
      toast.success("Application permanently removed");
      setShowDeleteConfirm(false);
      setApplicants((prev) => prev.filter(app => app.id !== id));
      if (activeId === id) setActiveId(null);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to delete application");
    } finally {
      setIsDeleting(false);
    }
  };

    // FIlter
    // 1. Add state and ref at top of component:
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filterRef = useRef(null);

    // 2. Click outside handler to close dropdown
    useEffect(() => {
    const handleClickOutside = (e) => {
        if (filterRef.current && !filterRef.current.contains(e.target)) {
        setIsFilterOpen(false);
        }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 3. Tab options configuration
    const filterTabs = [
    { id: 'ALL', label: 'All Files' },
    { id: 'PENDING', label: 'Pending' },
    { id: 'SCHEDULED', label: 'Testing' },
    { id: 'ACCEPTED', label: 'Admitted' }
    ];

    const selectedTab = filterTabs.find(t => t.id === filterStatus);

    const selectedApp = applicants.find(a => a.id === activeId);

    const getStatusStyles = (status) => {
        switch(status) {
        case 'PENDING': 
            return { bg: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-500' };
        case 'SCHEDULED': 
            return { bg: 'bg-indigo-50 text-indigo-800 border-indigo-200', dot: 'bg-indigo-500' };
        case 'ACCEPTED': 
            return { bg: 'bg-emerald-50 text-emerald-800 border-emerald-200', dot: 'bg-emerald-500' };
        default: 
            return { bg: 'bg-rose-50 text-rose-800 border-rose-200', dot: 'bg-rose-500' };
        }
    };

  const statuses = [
    { id: "PENDING", label: "Awaiting Voucher", icon: "⏳", color: "text-amber-600 bg-amber-50 border-amber-100" },
    { id: "SCHEDULED", label: "Test Booked", icon: "🗓️", color: "text-blue-600 bg-blue-50 border-blue-100" },
    { id: "ACCEPTED", label: "Admitted", icon: "✅", color: "text-emerald-600 bg-emerald-50 border-emerald-100" },
    { id: "REJECTED", label: "Refused", icon: "❌", color: "text-rose-600 bg-rose-50 border-rose-100" }
  ];

  const currentStatus = statuses.find(s => s.id === selectedApp?.application_status) || statuses[0];
  const isDisabled = actionLoading || isDeleting;

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentApplicants = filteredApplicants.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredApplicants.length / itemsPerPage);

  return (
    <div className="h-screen w-screen bg-[#f8f9fa] flex font-dynamic-title text-zinc-700 antialiased overflow-hidden selection:bg-zinc-900 selection:text-white">
      <Toaster position="top-right" />
      <Sidebar
        permissions={permissions}
        user={user}
        handleLogout={handleLogout}
        onOpenChangePassword={() => setIsModalOpen(true)}
      />

      {/* Main Container */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        <Header
          loadingPeriod={loadingPeriod}
          yearDisplay={yearDisplay}
          termDisplay={termDisplay}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          notifOpen={notifOpen}
          setNotifOpen={setNotifOpen}
          notificationDropdownRef={notificationDropdownRef}
        />

        {/* Dynamic Inspector Viewport */}
        <main className="p-3 md:p-4 flex-1 flex flex-col font-dynamic-title gap-3 min-h-0 overflow-y-auto">
            {/* Main Content Area Container */}
            <div className="flex-grow flex flex-col lg:flex-row overflow-hidden w-full bg-white border border-slate-200 rounded-xl shadow-xs min-h-[600px]">
                {/* Applicant Index List Sidebar */}
                <aside className="w-full lg:w-[360px] bg-white border-b lg:border-b-0 lg:border-r border-slate-200 flex flex-col flex-shrink-0">
                {/* Search & Custom Filter Bar */}
                <div className="p-3 border-b border-slate-100 flex items-center gap-2 bg-slate-50/30">
                    <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search name, phone..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs placeholder-slate-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition shadow-xs"
                    />
                    </div>

                    {/* Custom Dropdown Trigger & Menu */}
                    <div className="relative shrink-0" ref={filterRef}>
                    <button
                        type="button"
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-semibold text-xs py-2 px-3 rounded-xl transition shadow-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                        <span className="truncate max-w-[80px]">{selectedTab?.label}</span>
                        <span className="bg-indigo-50 text-indigo-600 px-1.5 py-0.5 text-[10px] rounded-md font-bold">
                        {categoryCounts[filterStatus] ?? 0}
                        </span>
                        <ChevronDown
                        className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${
                            isFilterOpen ? "rotate-180" : ""
                        }`}
                        />
                    </button>

                    {isFilterOpen && (
                        <div className="absolute right-0 mt-1.5 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                        {filterTabs.map((tab) => {
                            const isSelected = filterStatus === tab.id;
                            return (
                            <button
                                key={tab.id}
                                onClick={() => {
                                setFilterStatus(tab.id);
                                setCurrentPage(1);
                                setIsFilterOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold transition ${
                                isSelected
                                    ? "bg-slate-50 text-[#002855]"
                                    : "text-slate-600 hover:bg-slate-50/80 hover:text-slate-900"
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                <div
                                    className={`w-1.5 h-1.5 rounded-full ${
                                    isSelected ? "bg-blue-600" : "bg-transparent"
                                    }`}
                                />
                                <span className={isSelected ? "font-bold" : ""}>
                                    {tab.label}
                                </span>
                                </div>
                                <span
                                className={`px-1.5 py-0.5 text-[10px] rounded-md font-bold ${
                                    isSelected
                                    ? "bg-indigo-50 text-indigo-600"
                                    : "bg-slate-100 text-slate-500"
                                }`}
                                >
                                {categoryCounts[tab.id] ?? 0}
                                </span>
                            </button>
                            );
                        })}
                        </div>
                    )}
                    </div>
                </div>

                {/* Applicant Scroll Area */}
                <div className="flex-grow overflow-y-auto divide-y divide-slate-100">
                    {errorMessage && (
                    <div className="p-4 m-3 bg-rose-50 text-rose-700 text-xs font-medium rounded-xl border border-rose-100 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>{errorMessage}</span>
                    </div>
                    )}

                    {loading && applicants.length === 0 ? (
                    <div className="p-12 text-center text-xs text-slate-400 font-medium animate-pulse">
                        Loading register records...
                    </div>
                    ) : filteredApplicants.length === 0 ? (
                    <div className="p-12 text-center text-xs text-slate-400 font-medium">
                        No applicant records found.
                    </div>
                    ) : (
                    currentApplicants.map((app) => {
                        const isSelected = app.id === activeId;
                        const badge = getStatusStyles(app.application_status);
                        return (
                        <div
                            key={app.id}
                            onClick={() => setActiveId(app.id)}
                            className={`p-4 transition cursor-pointer relative flex flex-col gap-1.5 border-l-4 ${
                            isSelected
                                ? "bg-slate-50 border-[#002855]"
                                : "border-transparent hover:bg-slate-50/50"
                            }`}
                        >
                            <div className="flex justify-between items-start gap-2">
                            <div>
                                <h4 className="text-xs font-bold text-slate-900 tracking-tight uppercase">
                                {app.fullnameEN || "Unnamed Student"}
                                </h4>
                                <p className="font-dynamic-serif text-xs text-slate-400 font-medium mt-0.5">
                                {app.fullnameKH}
                                </p>
                            </div>
                            <span className={`w-2 h-2 mt-1.5 rounded-full ${badge.dot}`} />
                            </div>

                            <div className="flex items-center justify-between text-xs mt-1">
                            <span className="bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 rounded font-bold text-[10px]">
                                {app.apply_grade || "Grade N/A"}
                            </span>
                            <span className="text-slate-500 font-medium text-[11px]">
                                {app.contactNumber}
                            </span>
                            </div>
                        </div>
                        );
                    })
                    )}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="p-3 border-t border-slate-100 bg-slate-50/50 mt-auto flex items-center justify-between text-xs font-bold text-slate-600">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                        }
                        disabled={currentPage === totalPages}
                        className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    </div>
                )}
                </aside>

                {/* Detail View Container */}
                <div className="flex-1 overflow-y-auto p-6">
                {selectedApp ? (
                    <div className="space-y-6 max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-2 duration-200">
                    {/* Dynamic Profile Header Bar */}
                    <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                        <div className="h-12 w-12 bg-[#002855] text-white uppercase rounded-xl flex items-center justify-center font-bold text-xl shadow-sm shrink-0">
                            {selectedApp.fullnameEN ? selectedApp.fullnameEN.charAt(0) : "S"}
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                            <h2 className="text-base font-bold text-slate-900 tracking-tight">
                                {selectedApp.fullnameEN}
                            </h2>
                            {selectedApp.fullnameKH && (
                                <span className="font-dynamic-serif text-xs font-semibold px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                                {selectedApp.fullnameKH}
                                </span>
                            )}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 font-medium">
                            <span>{selectedApp.gender || "N/A"}</span>
                            <span className="text-slate-300">•</span>
                            <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                Born {selectedApp.dob || "N/A"}
                            </span>
                            </div>
                        </div>
                        </div>

                        {/* Inline Fast Action Bar */}
                        <div className="flex items-center gap-2 self-start sm:self-auto pt-3 sm:pt-0 border-t sm:border-t-0 border-slate-100 w-full sm:w-auto justify-end">
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider hidden sm:inline">
                            Status
                            </span>

                            <div className="relative inline-block text-left flex-1 sm:w-48">
                            <button
                                type="button"
                                disabled={isDisabled}
                                onClick={() => setIsOpen(!isOpen)}
                                className="w-full flex items-center justify-between bg-slate-50 border border-slate-200/80 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 hover:border-slate-300 focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-50 disabled:pointer-events-none"
                            >
                                <span className="flex items-center gap-2 truncate">
                                <span>{currentStatus.icon}</span>
                                <span className="truncate">{currentStatus.label}</span>
                                </span>
                                <ChevronDown
                                className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 shrink-0 ${
                                    isOpen ? "rotate-180" : ""
                                }`}
                                />
                            </button>

                            {isOpen && (
                                <div className="absolute right-0 mt-1.5 w-full min-w-[200px] origin-top-right rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl ring-1 ring-black/5 z-50 animate-in fade-in slide-in-from-top-1 duration-100 space-y-0.5">
                                {statuses.map((status) => {
                                    const isSelected =
                                    status.id === selectedApp.application_status;
                                    return (
                                    <button
                                        key={status.id}
                                        type="button"
                                        onClick={() => {
                                        handleStatusChange(selectedApp.id, status.id);
                                        setIsOpen(false);
                                        }}
                                        className={`w-full flex items-center justify-between px-3 py-2 text-xs rounded-lg transition-colors text-left font-medium ${
                                        isSelected
                                            ? "bg-indigo-50 text-indigo-600 font-semibold"
                                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                        }`}
                                    >
                                        <span className="flex items-center gap-2">
                                        <span>{status.icon}</span>
                                        <span>{status.label}</span>
                                        </span>
                                        {isSelected && (
                                        <Check className="w-3.5 h-3.5 text-indigo-600 stroke-[2.5]" />
                                        )}
                                    </button>
                                    );
                                })}
                                </div>
                            )}
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                            setSelectedStudent(selectedApp);
                            setIsDetailModalOpen(true);
                            }}
                            className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-100 rounded-xl transition"
                            title="View Details"
                        >
                            <Eye className="w-4 h-4" />
                        </button>

                        <button
                            type="button"
                            disabled={actionLoading || isDeleting}
                            onClick={() => setShowDeleteConfirm(true)}
                            className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 hover:border-rose-100 rounded-xl transition disabled:opacity-50"
                            title="Delete Application"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                        </div>
                    </div>

                    {/* Main Grid Section */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Left 2-Column: Application Overview & Family */}
                        <div className="md:col-span-2 space-y-6">
                        {/* Summary Card */}
                        <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-xs space-y-5">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2 border-b border-slate-100 pb-3">
                            <FileText className="w-4 h-4 text-slate-500" />
                            <span>Application Summary</span>
                            </h3>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div className="p-3.5 bg-slate-50/80 border border-slate-100 rounded-xl">
                                <p className="text-slate-400 font-semibold text-xs flex items-center gap-1.5">
                                <GraduationCap className="w-3.5 h-3.5 text-slate-400" />
                                Placement Target
                                </p>
                                <p className="font-bold text-slate-800 mt-1">
                                {selectedApp.apply_grade || "Unspecified"}
                                </p>
                            </div>

                            <div className="p-3.5 bg-slate-50/80 border border-slate-100 rounded-xl">
                                <p className="text-slate-400 font-semibold text-xs flex items-center gap-1.5">
                                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                                Previous School
                                </p>
                                <p className="font-dynamic-serif font-bold text-slate-800 mt-1 truncate">
                                {selectedApp.previous_school || "None Specified"}
                                </p>
                            </div>
                            </div>

                            <div className="bg-indigo-50/50 rounded-xl p-4 border border-indigo-100/80 text-sm">
                            <p className="font-bold text-indigo-950 flex items-center gap-1.5 text-xs uppercase tracking-wider">
                                <Info className="w-4 h-4 text-indigo-500" />
                                Enrollment Rationale
                            </p>
                            <p className="font-dynamic-serif text-slate-600 mt-2 text-xs leading-relaxed italic">
                                "{selectedApp.why_choose_cais || "No statement submitted."}"
                            </p>
                            </div>
                        </div>

                        {/* Family Directory */}
                        <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-xs space-y-4">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2 border-b border-slate-100 pb-3">
                            <Users className="w-4 h-4 text-indigo-500" />
                            <span>Family Directory</span>
                            </h3>

                            <div className="text-sm grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="bg-slate-50/50 border border-slate-100 rounded-xl p-4">
                                <p className="text-slate-400 text-xs font-semibold">
                                Mother's Name
                                </p>
                                <p className="font-bold text-slate-800 mt-1 uppercase">
                                {selectedApp.motherNameEN || "---"}
                                </p>
                                <p className="text-slate-500 font-medium mt-2 flex items-center gap-1.5 text-xs">
                                <Phone className="w-3.5 h-3.5 text-slate-400" />
                                {selectedApp.motherContact || "---"}
                                </p>
                            </div>

                            <div className="bg-slate-50/50 border border-slate-100 rounded-xl p-4">
                                <p className="text-slate-400 text-xs font-semibold">
                                Father's Name
                                </p>
                                <p className="font-bold text-slate-800 mt-1 uppercase">
                                {selectedApp.fatherNameEN || "---"}
                                </p>
                                <p className="text-slate-500 font-medium mt-2 flex items-center gap-1.5 text-xs">
                                <Phone className="w-3.5 h-3.5 text-slate-400" />
                                {selectedApp.fatherContact || "---"}
                                </p>
                            </div>
                            </div>
                        </div>
                        </div>

                        {/* Right 1-Column Sidebar Widgets */}
                        <div className="md:col-span-1 space-y-6">
                        {/* Medical Alert Card */}
                        <div className="bg-rose-50/40 border border-rose-200/60 rounded-2xl p-5 space-y-3">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-rose-800 flex items-center gap-2 border-b border-rose-200/40 pb-2.5">
                            <ShieldAlert className="w-4 h-4 text-rose-600" />
                            <span>Medical & Diet Notes</span>
                            </h4>
                            <p className="text-xs text-slate-600 leading-relaxed font-medium">
                            {selectedApp.health_record ||
                                "No clinical records or dietary restrictions specified."}
                            </p>
                        </div>

                        {/* Emergency Backup */}
                        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3 text-sm shadow-xs">
                            <h4 className="font-bold text-slate-400 uppercase text-[10px] tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-2.5">
                            <HeartHandshake className="w-4 h-4 text-slate-400" />
                            <span>Emergency Contact</span>
                            </h4>
                            <div className="space-y-2">
                            <div>
                                <p className="font-bold text-slate-800 text-xs">
                                {selectedApp.emergency_firstName}{" "}
                                {selectedApp.emergency_lastName}
                                </p>
                                <p className="text-slate-500 text-xs font-medium flex items-center gap-1.5 mt-1">
                                <Phone className="w-3.5 h-3.5 text-slate-400" />
                                {selectedApp.contactNumber || "---"}
                                </p>
                            </div>
                            <p className="font-dynamic-serif text-slate-400 text-xs leading-relaxed pt-2 border-t border-slate-100">
                                {selectedApp.emergency_address || "No address configured."}
                            </p>
                            </div>
                        </div>
                        </div>
                    </div>

                    {/* Verification Section */}
                    <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-xs space-y-4">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2 border-b border-slate-100 pb-3">
                        <FileCheck className="w-4 h-4 text-emerald-600" />
                        <span>Uploaded Verification Documents</span>
                        </h3>

                        {!selectedApp.documents || selectedApp.documents.length === 0 ? (
                        <div className="p-8 text-center border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                            <p className="text-xs font-medium text-slate-400">
                            No identity or academic documentation uploaded.
                            </p>
                        </div>
                        ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {selectedApp.documents.map((doc) => (
                            <div
                                key={doc.id}
                                className="border border-slate-200/80 bg-slate-50/30 hover:bg-slate-50/80 rounded-xl p-4 flex flex-col justify-between min-h-[110px] transition"
                            >
                                <div>
                                <span className="text-xs font-bold text-slate-800 capitalize block">
                                    {doc.document_type.replace(/_/g, " ")}
                                </span>
                                <p className="text-[10px] text-slate-400 font-medium mt-1">
                                    Uploaded:{" "}
                                    {new Date(doc.uploaded_at).toLocaleDateString()}
                                </p>
                                </div>

                                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                                <span
                                    className="text-[11px] text-slate-400 truncate flex-1"
                                    title={doc.file.split("/").pop()}
                                >
                                    {doc.file.split("/").pop()}
                                </span>
                                <a
                                    href={doc.file}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-100 px-2.5 py-1 rounded-lg text-[11px] font-semibold text-slate-700 transition shrink-0"
                                >
                                    <span>View</span>
                                    <ExternalLink className="w-3 h-3 text-slate-400" />
                                </a>
                                </div>
                            </div>
                            ))}
                        </div>
                        )}
                    </div>
                    </div>
                ) : (
                    /* Empty State */
                    <div className="h-full flex flex-col items-center justify-center text-slate-400 py-28 text-sm font-medium gap-3">
                    <div className="p-4 rounded-full bg-slate-100/80 border border-slate-200/60">
                        <FileText className="w-8 h-8 text-slate-400 stroke-[1.5]" />
                    </div>
                    <span className="text-slate-500 font-medium">
                        Select an applicant from the sidebar to view details.
                    </span>
                    </div>
                )}
                </div>
            </div>
            </main>
        
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-sm w-full p-6 space-y-4 border border-slate-100 shadow-xl">
                <div className="flex items-center gap-3 text-rose-600">
                <ShieldAlert className="w-6 h-6" />
                <h3 className="font-bold text-slate-900 text-sm">Purge Record Permanently?</h3>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                Are you sure you want to delete this applicant file? This action cannot be reversed.
                </p>
                <div className="flex justify-end gap-2 pt-2">
                <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100"
                >
                    Cancel
                </button>
                <button
                    disabled={isDeleting}
                    onClick={() => handleDelete(activeId)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white shadow-xs"
                >
                    {isDeleting ? "Deleting..." : "Confirm Delete"}
                </button>
                </div>
            </div>
            </div>
        )}

        {/* Detail Profile View Modal */}
        {isDetailModalOpen && selectedStudent && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-[100] flex items-center justify-center p-4 sm:p-6 antialiased">
            <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-xl shadow-2xl border border-slate-200/80 overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
            
            {/* ── Modal Header Banner ── */}
            <div className="bg-[#002855] text-white px-6 py-5 flex items-start justify-between relative shrink-0">
                <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                    <span className="bg-white/10 text-white/90 border border-white/20 px-2.5 py-0.5 rounded-md text-[11px] font-bold tracking-wide uppercase">
                    {selectedStudent.apply_grade || 'Grade N/A'}
                    </span>
                </div>
                <h3 className="text-lg sm:text-xl font-dynamic-serif font-extrabold tracking-tight text-white flex items-center gap-2">
                    <span>{selectedStudent.fullnameKH}</span>
                    <span className="text-blue-300 font-normal">|</span>
                    <span className="uppercase text-slate-200 text-base font-semibold">{selectedStudent.fullnameEN}</span>
                </h3>
                <p className="font-dynamic-serif text-xs text-blue-100/70 font-light">
                    ព័ត៌មានលម្អិតបេក្ខជន / Applicant Profile Details
                </p>
                </div>

                {/* Close Button */}
                <button 
                type="button" 
                onClick={() => {
                    setIsDetailModalOpen(false);
                    setSelectedStudent(null);
                }}
                className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-white/30"
                >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                </button>
            </div>

            {/* ── Main Scrollable Body Matrix ── */}
            <div className="p-6 overflow-y-auto space-y-6 text-xs text-slate-700 bg-slate-50/30">
                
                {/* Section 1: Student Core Demographics */}
                <section className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-100 text-[#002855]">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <h4 className="font-dynamic-serif text-xs font-bold uppercase tracking-wider">ព័ត៌មានផ្ទាល់ខ្លួនសិស្ស / Student Profile</h4>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                    <span className="text-slate-400 font-dynamic-serif font-medium block mb-1">ឈ្មោះខ្មែរ (Khmer Name)</span>
                    <p className="font-bold text-slate-900 font-dynamic-serif text-sm">{selectedStudent.fullnameKH || '—'}</p>
                    </div>
                    <div>
                    <span className="text-slate-400 font-medium font-dynamic-serif block mb-1">ឈ្មោះឡាតាំង (English Name)</span>
                    <p className="font-bold text-slate-900 text-sm font-dynamic-serif uppercase">{selectedStudent.fullnameEN || '—'}</p>
                    </div>
                    <div>
                    <span className="text-slate-400 font-medium font-dynamic-serif block mb-1">ថ្ងៃខែឆ្នាំកំណើត (DOB)</span>
                    <p className="font-semibold text-slate-800">{selectedStudent.dob || '—'}</p>
                    </div>
                    <div>
                    <span className="text-slate-400 font-medium font-dynamic-serif block mb-1">ភេទ (Gender)</span>
                    <p className="font-semibold text-slate-800">{selectedStudent.gender || '—'}</p>
                    </div>
                    <div>
                    <span className="text-slate-400 font-medium font-dynamic-serif block mb-1">សញ្ជាតិ (Nationality)</span>
                    <p className="font-semibold text-slate-800 font-dynamic-serif">{selectedStudent.nationality || '—'}</p>
                    </div>
                    <div className="md:col-span-3">
                    <span className="text-slate-400 font-medium font-dynamic-serif block mb-1">ទីកន្លែងកំណើត (Place of Birth)</span>
                    <p className="font-semibold text-slate-800 font-dynamic-serif">{selectedStudent.place_birth || '—'}</p>
                    </div>
                </div>
                </section>

                {/* Section 2: Academic Route & Health parameters */}
                <section className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-100 text-[#002855]">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0112 20.055a11.952 11.952 0 01-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                    </svg>
                    <h4 className="text-xs font-bold uppercase tracking-wider font-dynamic-serif">ប្រវត្តិសិក្សា និងវេជ្ជសាស្ត្រ / Placement Metrics</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                    <span className="text-slate-400 font-medium block mb-1 font-dynamic-serif">សាលាចាស់ (Previous School)</span>
                    <p className="font-semibold text-slate-800 font-dynamic-serif">{selectedStudent.previous_school || '—'}</p>
                    </div>
                    <div>
                    <span className="text-slate-400 font-medium block mb-1 font-dynamic-serif">ថ្នាក់សុំចូល (Apply Grade)</span>
                    <span className="inline-block px-2.5 py-1 bg-indigo-50 border border-indigo-100 text-indigo-700 font-bold rounded-lg text-xs">
                        {selectedStudent.apply_grade || '—'}
                    </span>
                    </div>
                    <div className="md:col-span-2">
                    <span className="text-slate-400 font-medium block mb-1 font-dynamic-serif">មូលហេតុជ្រើសរើសសាលា (Why CAIS)</span>
                    <p className="font-dynamic-serif text-slate-800 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-200/60 font-medium">
                        {selectedStudent.why_choose_cais || '—'}
                    </p>
                    </div>
                    <div className="md:col-span-2">
                    <span className="text-slate-400 font-medium block mb-1 font-dynamic-serif">កំណត់ត្រាសុខភាព (Health Profile)</span>
                    <p className="font-dynamic-serif text-slate-800 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-200/60 font-medium">
                        {selectedStudent.health_record || 'No specific condition listed / សុខភាពធម្មតា'}
                    </p>
                    </div>
                </div>
                </section>

                {/* Section 3: Parents Ledger Block */}
                <section className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-100 text-[#002855]">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h4 className="font-dynamic-serif text-xs font-bold uppercase tracking-wider">ព័ត៌មានអាណាព្យាបាល / Family Matrix</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Mother Card */}
                    <div className="bg-slate-50/70 p-4 rounded-xl border border-slate-200/60 space-y-2">
                    <span className="font-dynamic-serif font-bold text-[#002855] border-b border-slate-200 pb-1.5 block text-xs">
                        👩 មាតា / Mother Details
                    </span>
                    <div className="space-y-1 pt-1">
                        <p className="font-dynamic-serif"><span className="text-slate-400">ឈ្មោះ (Name):</span> <strong className="text-slate-800">{selectedStudent.motherNameKH || '—'}</strong> <span className="text-slate-500">({selectedStudent.motherNameEN?.toUpperCase() || '—'})</span></p>
                        <p className="font-dynamic-serif"><span className="text-slate-400">ទូរស័ព្ទ (Phone):</span> <strong className="text-indigo-600 font-mono">{selectedStudent.motherContact || '—'}</strong></p>
                        <p className="font-dynamic-serif"><span className="text-slate-400">មុខរបរ (Occupation):</span> <span className="text-slate-700">{selectedStudent.motherOccupation || '—'}</span></p>
                        <p className="font-dynamic-serif"><span className="text-slate-400">សាសនា (Religion):</span> <span className="text-slate-700">{selectedStudent.motherReligion || '—'}</span></p>
                    </div>
                    </div>

                    {/* Father Card */}
                    <div className="bg-slate-50/70 p-4 rounded-xl border border-slate-200/60 space-y-2">
                    <span className="font-dynamic-serif font-bold text-[#002855] border-b border-slate-200 pb-1.5 block text-xs">
                        👨 ឪពុក / Father Details
                    </span>
                    <div className="space-y-1 pt-1">
                        <p className="font-dynamic-serif"><span className="text-slate-400">ឈ្មោះ (Name):</span> <strong className="text-slate-800">{selectedStudent.fatherNameKH || '—'}</strong> <span className="text-slate-500">({selectedStudent.fatherNameEN?.toUpperCase() || '—'})</span></p>
                        <p className="font-dynamic-serif"><span className="text-slate-400">ទូរស័ព្ទ (Phone):</span> <strong className="text-indigo-600 font-mono">{selectedStudent.fatherContact || '—'}</strong></p>
                        <p className="font-dynamic-serif"><span className="text-slate-400">មុខរបរ (Occupation):</span> <span className="text-slate-700">{selectedStudent.fatherOccupation || '—'}</span></p>
                        <p className="font-dynamic-serif"><span className="text-slate-400">សាសនា (Religion):</span> <span className="text-slate-700">{selectedStudent.fatherReligion || '—'}</span></p>
                    </div>
                    </div>
                </div>
                </section>

                {/* Section 4: Residential & Emergency */}
                <section className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-100 text-[#002855]">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <h4 className="text-xs font-bold uppercase tracking-wider font-dynamic-serif">អាសយដ្ឋាន និងទំនាក់ទំនងអាសន្ន / Residential & Emergency</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                    <span className="text-slate-400 font-medium block mb-1 font-dynamic-serif">ទូរស័ព្ទចម្បងគ្រួសារ</span>
                    <p className="font-bold text-slate-900 font-mono">{selectedStudent.contactNumber || '—'}</p>
                    </div>
                    <div className="md:col-span-2">
                    <span className="text-slate-400 font-medium block mb-1 font-dynamic-serif">អាសយដ្ឋានបច្ចុប្បន្ន (Address)</span>
                    <p className="font-semibold text-slate-800 font-dynamic-serif">{selectedStudent.address || '—'}</p>
                    </div>
                    <div className="md:col-span-3 pt-3 border-t border-slate-100">
                    <span className="text-slate-400 font-medium block mb-1 font-dynamic-serif">អ្នកធានាករណីអាសន្ន (Emergency Contact Representative)</span>
                    <p className="font-bold text-slate-900">
                        {selectedStudent.emergency_firstName} {selectedStudent.emergency_lastName} 
                        <span className="font-dynamic-serif text-slate-500 font-normal ml-2">({selectedStudent.emergency_address || 'No Address Listed'})</span>
                    </p>
                    </div>
                </div>
                </section>

            </div>

            {/* ── Modal Footer Controls ── */}
            <div className="bg-white px-6 py-4 border-t border-slate-200/80 flex items-center justify-between shrink-0">
                <span className="text-[11px] text-slate-400 font-medium">
                Confidential Student Record — Internal Use Only
                </span>
                <button 
                type="button" 
                onClick={() => {
                    setIsDetailModalOpen(false);
                    setSelectedStudent(null);
                }}
                className="px-6 py-2.5 bg-[#002855] hover:bg-slate-900 text-white text-xs font-bold rounded-xl shadow-sm hover:shadow transition-all focus:outline-none focus:ring-2 focus:ring-slate-900/20"
                >
                Close Record View
                </button>
            </div>

            </div>
        </div>
        )}
    </div>
  );
}